import { combineReducers } from "@reduxjs/toolkit";

import type { ClusterItemAction } from "domains/clusters/actionTypes";
import { ClusterActionType } from "domains/clusters/actionTypes";
import type { CustomMapAction } from "domains/createMapDetail/actionTypes";
import { CreateMapTypes } from "domains/createMapDetail/actionTypes";
import type { CustomKeyIssueTopicSearchFulfilledAction } from "domains/customKeyIssue/actionTypes";
import { CustomKeyIssueTypes } from "domains/customKeyIssue/actionTypes";
import type { FetchTopicsSearchFulfilledAction } from "domains/search/actionTypes";
import { SearchType } from "domains/search/actionTypes";
import type { TopicAction } from "entities/topics/actionTypes";
import { TopicActionType } from "entities/topics/actionTypes";
import { getPersistedImages } from "entities/topics/helpers/getPersistedImages";
import { addUserOrLanguageChangeReset } from "entities/topics/reducerHelpers";
import type { Topic, TopicsState } from "entities/topics/types";

const initialState: TopicsState = {
  byId: {},
  following: { hasNextPage: false, ids: [], isPending: false, limit: 8, offset: 0 },
  highlightedIds: [],
  isFetching: { byType: {}, highlighted: false },
  sharedWithMe: { hasNextPage: false, ids: [], isPending: false, limit: 8, offset: 0 },
};

type Action = TopicAction | ClusterItemAction | CustomKeyIssueTopicSearchFulfilledAction;

const byId = (
  state = initialState.byId,
  action: Action | FetchTopicsSearchFulfilledAction | CustomMapAction,
): TopicsState["byId"] => {
  switch (action.type) {
    case TopicActionType.FETCH_TOPIC_FULFILLED: {
      const { topic } = action.payload;
      const dimensions = topic.dimensions ?? [];
      const dimensionsById = dimensions
        .reduce((accumulator, next) => [...accumulator, ...(next.topics ?? [])], dimensions)
        .reduce<Partial<Record<string, Topic>>>(
          (accumulator, next) => ({
            ...accumulator,
            [next.id]: { ...state[next.id], ...next, isTMLoaded: state[next.id]?.isTMLoaded ?? false } satisfies Topic,
          }),
          {},
        );
      const newImages = topic.images;
      const existingImages = state[topic.id]?.images ?? null;
      const images =
        existingImages === null ? newImages : newImages === null ? null : getPersistedImages(newImages, existingImages);
      return {
        ...state,
        ...dimensionsById,
        [topic.id]: {
          ...topic,
          images,
          isTMLoaded: true,
        },
      };
    }

    case TopicActionType.FETCH_TOPIC_REJECTED: {
      const { id } = action.payload;
      const topic = state[id];
      if (topic === undefined) return state;
      return {
        ...state,
        [id]: {
          ...topic,
          isTMLoaded: false,
        },
      };
    }

    case TopicActionType.FETCH_FOLLOWED_LIST_FULFILLED:
    case TopicActionType.FETCH_HIGHLIGHTS_FULFILLED:
    case TopicActionType.FETCH_TOPICS_FULFILLED: {
      return (
        action.payload.topics?.reduce<TopicsState["byId"]>((accumulator, next) => {
          const existingTopic = state[next.id];
          const attachment =
            next.attachment === undefined
              ? existingTopic?.attachment
              : { ...existingTopic?.attachment, ...next.attachment };
          const newImages = next.images;
          const existingImages = existingTopic?.images ?? null;
          const images =
            existingImages === null
              ? newImages
              : newImages === null
                ? null
                : getPersistedImages(newImages, existingImages);
          return {
            ...accumulator,
            [next.id]: {
              ...existingTopic,
              ...next,
              attachment,
              images,
              isTMLoaded: existingTopic?.isTMLoaded ?? false,
            } satisfies Topic,
          };
        }, state) ?? state
      );
    }

    case TopicActionType.FOLLOW:
    case TopicActionType.UNFOLLOW: {
      const { id } = action.payload;
      const topic = state[id];
      if (topic === undefined) return state;
      return {
        ...state,
        [id]: {
          ...topic,
          isFollowingUpdating: true,
        },
      };
    }

    case TopicActionType.FOLLOW_FULFILLED:
    case TopicActionType.FOLLOW_REJECTED:
    case TopicActionType.UNFOLLOW_FULFILLED:
    case TopicActionType.UNFOLLOW_REJECTED: {
      const { id } = action.payload;
      const topic = state[id];
      if (topic === undefined) return state;
      return {
        ...state,
        [id]: {
          ...topic,
          is_following: action.payload.isFollowing ?? topic.is_following,
          isFollowingUpdating: false,
        },
      };
    }

    case ClusterActionType.FETCH_FULFILLED: {
      return {
        ...state,
        ...action.payload.related_topics.reduce<Partial<Record<string, Topic>>>(
          (accumulator, next) => ({
            ...accumulator,
            [next.id]: { ...state[next.id], ...next } satisfies Topic,
          }),
          {},
        ),
      };
    }

    case CustomKeyIssueTypes.FETCH_TOPIC_SEARCH_FULFILLED:
    case SearchType.FETCH_TOPICS_FULFILLED: {
      return (action.payload ?? []).reduce<TopicsState["byId"]>(
        (byId, topic) => ({
          ...byId,
          [topic.id]: {
            ...state[topic.id],
            ...topic,
            is_following: topic.is_following ?? state[topic.id]?.is_following ?? false,
          } satisfies Topic,
        }),
        state,
      );
    }

    case CreateMapTypes.UPLOAD_FULFILLED: {
      const { id, images } = action.payload;
      const topic = state[id];
      return topic === undefined ? state : { ...state, [id]: { ...topic, images } };
    }

    case TopicActionType.TOGGLE_TOPIC_MACHINE_TRANSLATION: {
      const { id } = action.meta;
      const topic = state[id];
      if (!topic?.is_machine_translated) return state;

      const newTopic = { ...topic };

      // Initialise the additional 'translated' state on the topic if it doesn't already exist
      if (newTopic.name_translated === undefined) {
        newTopic.description_translated = topic.description;
        newTopic.isMachineTranslationsOff = false;
        newTopic.name_translated = topic.name;
        newTopic.subtitle_translated = topic.subtitle;
      }

      if (newTopic.isMachineTranslationsOff) {
        // Toggle machine translations back on again (revert to initial state)
        newTopic.description = topic.description_translated;
        newTopic.isMachineTranslationsOff = false;
        // `topic.name_translated` should never actually be undefined here
        newTopic.name = topic.name_translated ?? topic.name;
        newTopic.subtitle = topic.subtitle_translated;
      } else {
        // Toggle machine translations off
        newTopic.description = topic.description_original ?? topic.description;
        newTopic.isMachineTranslationsOff = true;
        newTopic.name = topic.name_original ?? topic.name;
        newTopic.subtitle = topic.subtitle_original ?? topic.subtitle;
      }

      return { ...state, [id]: newTopic };
    }

    default:
      return state;
  }
};

const highlightedIds = (state = initialState.highlightedIds, action: TopicAction): TopicsState["highlightedIds"] => {
  switch (action.type) {
    case TopicActionType.FETCH_HIGHLIGHTS_FULFILLED:
      return action.payload.topics.map(({ id }) => id);
    default:
      return state;
  }
};

const following = (state = initialState.following, action: TopicAction): TopicsState["following"] => {
  switch (action.type) {
    case TopicActionType.FETCH_FOLLOWED_LIST: {
      return {
        ...state,
        isPending: true,
      };
    }
    case TopicActionType.FETCH_FOLLOWED_LIST_FULFILLED: {
      const fetchedTopicIds = action.payload.topics.map(({ id }) => id);
      const newIds = action.payload.offset ? [...state.ids, ...fetchedTopicIds] : fetchedTopicIds;

      return {
        ...state,
        isPending: false,
        ids: newIds,
        hasNextPage: action.payload.topics.length === action.payload.limit,
        limit: action.payload.limit,
        offset: action.payload.offset,
      };
    }
    case TopicActionType.FETCH_FOLLOWED_LIST_REJECTED: {
      return {
        ...state,
        isPending: false,
      };
    }
    case TopicActionType.FOLLOW_FULFILLED: {
      return {
        ...state,
        ids: [...state.ids, action.payload.id],
      };
    }
    case TopicActionType.UNFOLLOW_FULFILLED: {
      return {
        ...state,
        ids: state.ids.filter((id) => id !== action.payload.id),
      };
    }

    default:
      return state;
  }
};

const sharedWithMe = (state = initialState.sharedWithMe, action: TopicAction): TopicsState["sharedWithMe"] => {
  switch (action.type) {
    case TopicActionType.FETCH_SHARED_WITH_ME: {
      return {
        ...state,
        isPending: true,
      };
    }
    case TopicActionType.FETCH_SHARED_WITH_ME_FULFILLED: {
      const newIds = action.payload.offset ? [...state.ids, ...action.payload.topicIds] : action.payload.topicIds;

      return {
        ...state,
        isPending: false,
        ids: newIds,
        hasNextPage: action.payload.topicIds.length === action.payload.limit,
        limit: action.payload.limit,
        offset: action.payload.offset,
      };
    }
    case TopicActionType.FETCH_SHARED_WITH_ME_REJECTED: {
      return {
        ...state,
        isPending: false,
      };
    }
    default:
      return state;
  }
};

const isFetchingByType = (
  state = initialState.isFetching.byType,
  action: TopicAction,
): TopicsState["isFetching"]["byType"] => {
  switch (action.type) {
    case TopicActionType.FETCH_OF_TYPE:
      return {
        ...state,
        [action.payload.type]: true,
      };
    case TopicActionType.FETCH_TOPICS_FULFILLED:
    case TopicActionType.FETCH_TOPICS_REJECTED:
      return {
        ...state,
        [action.payload.type]: false,
      };

    default:
      return state;
  }
};

const isFetchingHighlighted = (
  state = initialState.isFetching.highlighted,
  action: TopicAction,
): TopicsState["isFetching"]["highlighted"] => {
  switch (action.type) {
    case TopicActionType.FETCH_HIGHLIGHTS:
      return true;
    case TopicActionType.FETCH_HIGHLIGHTS_FULFILLED:
    case TopicActionType.FETCH_HIGHLIGHTS_REJECTED:
      return false;

    default:
      return state;
  }
};

export const topicsReducer = combineReducers({
  byId: addUserOrLanguageChangeReset(byId, initialState.byId),
  following: addUserOrLanguageChangeReset(following, initialState.following),
  highlightedIds: addUserOrLanguageChangeReset(highlightedIds, initialState.highlightedIds),
  isFetching: addUserOrLanguageChangeReset(
    combineReducers({ byType: isFetchingByType, highlighted: isFetchingHighlighted }),
    initialState.isFetching,
  ),
  sharedWithMe: addUserOrLanguageChangeReset(sharedWithMe, initialState.sharedWithMe),
});
